import {Dimensions, Platform, useWindowDimensions} from 'react-native';

const useResponsiveDimensions = () => {
  const dimensions = useWindowDimensions();

  /**
   * Returns the value based on the screen height percentage.
   * @param {number} percent - The percentage value relative to the screen height.
   * @returns {number} - The calculated value based on the screen height.
   */
  const hdp = (percent: number) => {
    return (
      ((Platform.OS === 'web'
        ? dimensions.height
        : Dimensions.get('screen').height) *
        percent) /
      100
    );
  };

  /**
   * Returns the value based on the screen width percentage.
   * @param {number} percent - The percentage value relative to the screen width.
   * @returns {number} - The calculated value based on the screen width.
   */
  const wdp = (percent: number) => {
    return (dimensions.width * percent) / 100;
  };

  /**
   * Returns the value based on the screen size.
   * @param {number} smValue - The value to be used for small screens.
   * @param {number} lgValue - The value to be used for large screens.
   * @param {number} xsmValue - The value to be used for very small screens.
   * @returns {number} - The calculated value based on the screen size.
   */
  const getByScreenSize = (
    smValue: any,
    lgValue: any,
    xsmValue: any = smValue,
  ): any => {
    return dimensions.width < 380
      ? xsmValue
      : !isLargeScreen
        ? smValue
        : lgValue;
  };

  /**
   * Returns the value based on the screen size with more breakpoints.
   * @param {number} small - The value to be used for small screens.
   * @param {number} large - The value to be used for large screens.
   * @param {number} medium - The value to be used for medium screens.
   * @returns {number} - The calculated value based on the screen size.
   */
  const getResponsiveSize = (small: number, large: number, medium?: number) => {
    if (dimensions.width >= 1024) return large;
    if (dimensions.width >= 768 && medium !== undefined) return medium;
    return small;
  };

  /**
   * Returns the value based on the screen height.
   * @param {number} smValue - The value to be used for small screens.
   * @param {number} lgValue - The value to be used for large screens.
   * @returns {number} - The calculated value based on the screen height.
   */
  const getByScreenHeight = (smValue: number, lgValue: number) => {
    return dimensions.height < 800 ? smValue : lgValue;
  };

  /**
   * Derived screen size helpers
   */
  const isMobile = dimensions.width < 768;
  const isLargeScreen = dimensions.width >= 768;

  return {
    hdp,
    wdp,
    getByScreenSize,
    getResponsiveSize,
    getByScreenHeight,
    isMobile,
    isLargeScreen,
    width: dimensions.width,
    height:
      Platform.OS === 'web'
        ? dimensions.height
        : Dimensions.get('screen').height,
  };
};

export default useResponsiveDimensions;
